import { AccountInfo, Configuration, PublicClientApplication } from '@azure/msal-browser';
import { config } from '../config/env';

const buildMsalConfig = (clientId: string): Configuration => ({
  auth: {
    authority: 'https://login.microsoftonline.com/common',
    clientId,
    navigateToLoginRequestUrl: false,
    redirectUri: `${window.location.protocol}//${window.location.host}/initialization`,
    postLogoutRedirectUri: `${window.location.protocol}//${window.location.host}/`,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
});

let msalInstance: PublicClientApplication | null = null;
let initPromise: Promise<PublicClientApplication | null> | null = null;

/**
 * Idempotent MSAL bootstrap. Creates the singleton client, awaits
 * `initialize()` and processes the redirect hash exactly once
 * (`handleRedirectPromise`). Subsequent calls return the same promise so we
 * never double-initialize under React StrictMode or HMR.
 */
export const initializeMsalForSignUp = (): Promise<PublicClientApplication | null> => {
  if (initPromise) return initPromise;

  if (!config.mainAppId) {
    initPromise = Promise.resolve(null);
    return initPromise;
  }

  initPromise = (async () => {
    const instance = new PublicClientApplication(buildMsalConfig(config.mainAppId));
    await instance.initialize();
    // Consume the redirect hash exactly once at startup. After this, code
    // reads accounts via `getAllAccounts()` — calling handleRedirectPromise
    // multiple times leads to no-op nulls that look like "not logged in".
    const result = await instance.handleRedirectPromise();
    // Make the account from the just-completed login the active one. This
    // matters for "change account" (loginRedirect with prompt=select_account):
    // when several accounts are cached, the freshly picked one must win over
    // an arbitrary getAllAccounts()[0].
    if (result?.account) {
      instance.setActiveAccount(result.account);
    } else if (!instance.getActiveAccount()) {
      const [first] = instance.getAllAccounts();
      if (first) instance.setActiveAccount(first);
    }
    msalInstance = instance;
    return instance;
  })();

  return initPromise;
};

export const getMsalInstance = (): PublicClientApplication | null => msalInstance;

export const acquireSignUpAccessToken = async (account: AccountInfo): Promise<string> => {
  const instance = getMsalInstance();
  if (!instance || !config.mainAppId) {
    throw new Error('MSAL not initialized or main app id not configured');
  }
  const result = await instance.acquireTokenSilent({
    account,
    scopes: [`${config.mainAppId}/.default`],
  });
  return result.accessToken;
};
